import type { WorkspaceUser } from '@/api/types'

/**
 * What to call somebody in the member list.
 *
 * `name` is the account's first name and is routinely blank -- the identity
 * provider is not obliged to send one, and nothing in this application asks for
 * it. The address is always there, so it is the fallback rather than a second
 * line that is sometimes the only line.
 */
export function memberLabel(member: WorkspaceUser): string {
  const name = member.name.trim()
  return name === '' ? member.email : name
}

const COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base' })

/**
 * The member list, in reading order.
 *
 * Sorted here rather than asked for sorted. These rows arrive inside the
 * workspace list, whose order is the database's, and the dedicated endpoint that
 * takes a `sorts` parameter is admin-only -- so a member would see an unsorted
 * list and an admin a sorted one, for want of a comparison over a handful of
 * rows already in memory.
 *
 * `Intl.Collator` rather than `<`: the members of a workspace are people, and
 * comparing their names by UTF-16 code unit puts every accented letter after Z.
 * The email tie-break keeps two people who share a first name in a stable order
 * across renders.
 */
export function sortMembers(members: readonly WorkspaceUser[]): WorkspaceUser[] {
  return [...members].sort(
    (a, b) =>
      COLLATOR.compare(memberLabel(a), memberLabel(b)) ||
      COLLATOR.compare(a.email, b.email),
  )
}
